import type { CalculationResponse, ReportSection } from '../../gen/ejot/roofing/v1/types';

export interface ReportPreviewSummary {
  statusLabel: string;
  basisLabel: string;
  moduleCount: number;
  zoneCount: number;
  selectedSectionCount: number;
  drawingMode: string;
  qualification: string;
}

export function buildReportPreviewSummary(
  result: CalculationResponse | undefined,
  sections: readonly ReportSection[],
  selectedIds: readonly string[],
  includeDrawings: boolean,
): ReportPreviewSummary {
  const modules = result?.modules ?? [];
  const bases = new Set(modules.map((module) => module.resistance_basis).filter(Boolean));
  const etaPullOut = bases.size === 1 && bases.has('FIXING_RESISTANCE_BASIS_ETA_COMPONENT_CHARACTERISTIC');
  const mandatory = sections.filter((section) => section.mandatory).map((section) => section.id ?? '');
  const included = new Set([...selectedIds, ...mandatory].filter(Boolean));

  return {
    statusLabel: result?.status ?? 'CALCULATION_STATUS_NOT_CALCULATED',
    basisLabel: etaPullOut
      ? 'ETA pull-out basis'
      : bases.size === 0 ? 'No calculated resistance basis' : [...bases].join(' + '),
    moduleCount: modules.length,
    zoneCount: modules.reduce((total, module) => total + (module.zones?.length ?? 0), 0),
    selectedSectionCount: included.size,
    drawingMode: includeDrawings ? 'Backend-generated deterministic drawings included' : 'Drawings excluded by report settings',
    qualification: etaPullOut
      ? 'Fixing density is based on ETA/EAD fastener pull-out design resistance. No verified complete membrane/fixing-system wind-uplift resistance or system PASS is claimed.'
      : result?.status === 'CALCULATION_STATUS_PASS'
        ? 'The report reproduces the supplied authoritative calculation result.'
        : 'The report records a result that is not a PASS and must preserve its warnings or blockers.',
  };
}
